/**
 * Accès Supabase par entité — API groupée `supabasePersistence` + helpers legacy `{ data, error }`.
 */
import { supabase, isSupabaseConfigured } from "./supabase";
import {
  SUPABASE_LIST_LIMIT,
  SELECT_CLIENTS_LIST,
  SELECT_EVENTS_LIST,
  SELECT_PRODUCTS_LIST,
  SELECT_PAYMENTS_LIST,
  SELECT_CLIENT_PRODUCTS_LIST,
  SELECT_COACH_ROW,
} from "../constants/supabaseSelectLists";
import { logger, toLogError } from "../utils/logger";

function logErr(ctx, err) {
  logger.error(`Persistence ${ctx}`, toLogError(err));
}

function guard() {
  if (!isSupabaseConfigured() || !supabase) return null;
  return supabase;
}

function sbOrThrow() {
  const sb = guard();
  if (!sb) throw new Error("Supabase not configured");
  return sb;
}

// ── Legacy soft API (ne throw pas) ─────────────────────────────────────────

export async function getClients(coachId) {
  const sb = guard();
  if (!sb || !coachId) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb
      .from("clients")
      .select(SELECT_CLIENTS_LIST)
      .eq("coach_id", coachId)
      .order("created_at", { ascending: false })
      .limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return { data, error: null };
  } catch (e) {
    logErr("getClients", e);
    return { data: null, error: e };
  }
}

export async function upsertClient(row) {
  const sb = guard();
  if (!sb) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb.from("clients").upsert(row, { onConflict: "id" }).select(SELECT_CLIENTS_LIST).single();
    if (error) throw error;
    return { data, error: null };
  } catch (e) {
    logErr("upsertClient", e);
    return { data: null, error: e };
  }
}

export async function deleteClient(clientId, coachId) {
  const sb = guard();
  if (!sb || !clientId) return { error: new Error("no supabase") };
  try {
    let q = sb.from("clients").delete().eq("id", clientId);
    if (coachId) q = q.eq("coach_id", coachId);
    const { error } = await q;
    if (error) throw error;
    return { error: null };
  } catch (e) {
    logErr("deleteClient", e);
    return { error: e };
  }
}

export async function getEvents(coachId) {
  const sb = guard();
  if (!sb || !coachId) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb
      .from("events")
      .select(SELECT_EVENTS_LIST)
      .eq("coach_id", coachId)
      .order("date", { ascending: true })
      .limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return { data, error: null };
  } catch (e) {
    logErr("getEvents", e);
    return { data: null, error: e };
  }
}

export async function upsertEvent(row) {
  const sb = guard();
  if (!sb) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb.from("events").upsert(row, { onConflict: "id" }).select(SELECT_EVENTS_LIST).single();
    if (error) throw error;
    return { data, error: null };
  } catch (e) {
    logErr("upsertEvent", e);
    return { data: null, error: e };
  }
}

export async function deleteEvent(eventId, coachId) {
  const sb = guard();
  if (!sb || !eventId) return { error: new Error("no supabase") };
  try {
    let q = sb.from("events").delete().eq("id", eventId);
    if (coachId) q = q.eq("coach_id", coachId);
    const { error } = await q;
    if (error) throw error;
    return { error: null };
  } catch (e) {
    logErr("deleteEvent", e);
    return { error: e };
  }
}

export async function getProducts(coachId) {
  const sb = guard();
  if (!sb || !coachId) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb.from("products").select(SELECT_PRODUCTS_LIST).eq("coach_id", coachId).limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return { data, error: null };
  } catch (e) {
    logErr("getProducts", e);
    return { data: null, error: e };
  }
}

export async function upsertProduct(row) {
  const sb = guard();
  if (!sb) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb.from("products").upsert(row, { onConflict: "id" }).select(SELECT_PRODUCTS_LIST).single();
    if (error) throw error;
    return { data, error: null };
  } catch (e) {
    logErr("upsertProduct", e);
    return { data: null, error: e };
  }
}

export async function getPayments(coachId) {
  const sb = guard();
  if (!sb || !coachId) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb
      .from("payments")
      .select(SELECT_PAYMENTS_LIST)
      .eq("coach_id", coachId)
      .order("created_at", { ascending: false })
      .limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return { data, error: null };
  } catch (e) {
    logErr("getPayments", e);
    return { data: null, error: e };
  }
}

export async function upsertPayment(row) {
  const sb = guard();
  if (!sb) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb.from("payments").upsert(row, { onConflict: "id" }).select(SELECT_PAYMENTS_LIST).single();
    if (error) throw error;
    return { data, error: null };
  } catch (e) {
    logErr("upsertPayment", e);
    return { data: null, error: e };
  }
}

export async function getCoachSettingsRow(coachId) {
  const sb = guard();
  if (!sb || !coachId) return { data: null, error: new Error("no supabase") };
  try {
    const { data, error } = await sb.from("coaches").select("settings").eq("id", coachId).single();
    if (error) throw error;
    return { data: data?.settings ?? {}, error: null };
  } catch (e) {
    logErr("getCoachSettingsRow", e);
    return { data: null, error: e };
  }
}

export async function updateCoachSettings(coachId, settings) {
  const sb = guard();
  if (!sb || !coachId) return { error: new Error("no supabase") };
  try {
    const { error } = await sb.from("coaches").update({ settings }).eq("id", coachId);
    if (error) throw error;
    return { error: null };
  } catch (e) {
    logErr("updateCoachSettings", e);
    return { error: e };
  }
}

// ── API groupée (brief) — throw si erreur ─────────────────────────────────

export const supabasePersistence = {
  async getClients(coachId) {
    const sb = sbOrThrow();
    const { data, error } = await sb
      .from("clients")
      .select(SELECT_CLIENTS_LIST)
      .eq("coach_id", coachId)
      .order("created_at", { ascending: false })
      .limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return data || [];
  },

  async insertClient(client) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("clients").insert(client).select(SELECT_CLIENTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async updateClient(id, updates) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("clients").update(updates).eq("id", id).select(SELECT_CLIENTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async deleteClient(id, coachId) {
    const sb = sbOrThrow();
    let q = sb.from("clients").delete().eq("id", id);
    if (coachId) q = q.eq("coach_id", coachId);
    const { error } = await q;
    if (error) throw error;
  },

  async getEvents(coachId) {
    const sb = sbOrThrow();
    const { data, error } = await sb
      .from("events")
      .select(SELECT_EVENTS_LIST)
      .eq("coach_id", coachId)
      .order("date", { ascending: true })
      .limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return data || [];
  },

  async insertEvent(event) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("events").insert(event).select(SELECT_EVENTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async updateEvent(id, updates) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("events").update(updates).eq("id", id).select(SELECT_EVENTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async deleteEvent(id, coachId) {
    const sb = sbOrThrow();
    let q = sb.from("events").delete().eq("id", id);
    if (coachId) q = q.eq("coach_id", coachId);
    const { error } = await q;
    if (error) throw error;
  },

  async getProducts(coachId) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("products").select(SELECT_PRODUCTS_LIST).eq("coach_id", coachId).limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return data || [];
  },

  async insertProduct(product) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("products").insert(product).select(SELECT_PRODUCTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async updateProduct(id, updates) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("products").update(updates).eq("id", id).select(SELECT_PRODUCTS_LIST).single();
    if (error) throw error;
    return data;
  },

  /**
   * Lignes `client_products` (Supabase). Le statut SQL `paused` (migration 20260325) n’est pas encore
   * mappé 1:1 sur l’UI React : la pause programme côté app passe par `productInstance.programmeState` / flux cancel.
   */
  async getClientProducts(coachId) {
    const sb = sbOrThrow();
    const { data, error } = await sb
      .from("client_products")
      .select(SELECT_CLIENT_PRODUCTS_LIST)
      .eq("coach_id", coachId)
      .limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return data || [];
  },

  async insertClientProduct(cp) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("client_products").insert(cp).select(SELECT_CLIENT_PRODUCTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async updateClientProduct(id, updates) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("client_products").update(updates).eq("id", id).select(SELECT_CLIENT_PRODUCTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async getPayments(coachId) {
    const sb = sbOrThrow();
    const { data, error } = await sb
      .from("payments")
      .select(SELECT_PAYMENTS_LIST)
      .eq("coach_id", coachId)
      .order("created_at", { ascending: false })
      .limit(SUPABASE_LIST_LIMIT);
    if (error) throw error;
    return data || [];
  },

  async insertPayment(payment) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("payments").insert(payment).select(SELECT_PAYMENTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async updatePayment(id, updates) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("payments").update(updates).eq("id", id).select(SELECT_PAYMENTS_LIST).single();
    if (error) throw error;
    return data;
  },

  async getCoach(coachId) {
    const sb = sbOrThrow();
    const { data, error } = await sb.from("coaches").select(SELECT_COACH_ROW).eq("id", coachId).single();
    if (error) throw error;
    return data;
  },

  async updateCoachSettings(coachId, settings) {
    const sb = sbOrThrow();
    const { error } = await sb.from("coaches").update({ settings }).eq("id", coachId);
    if (error) throw error;
  },

  async updateCoach(coachId, updates) {
    const sb = sbOrThrow();
    const { error } = await sb.from("coaches").update(updates).eq("id", coachId);
    if (error) throw error;
  },

  async hydrateAll(coachId) {
    const [clients, events, products, clientProducts, payments, coach] = await Promise.all([
      supabasePersistence.getClients(coachId),
      supabasePersistence.getEvents(coachId),
      supabasePersistence.getProducts(coachId),
      supabasePersistence.getClientProducts(coachId),
      supabasePersistence.getPayments(coachId),
      supabasePersistence.getCoach(coachId),
    ]);
    return {
      clients,
      events,
      products,
      clientProducts,
      payments,
      coach,
      settings: coach?.settings || {},
    };
  },
};

export const persistenceApi = {
  getClients,
  upsertClient,
  deleteClient,
  getEvents,
  upsertEvent,
  deleteEvent,
  getProducts,
  upsertProduct,
  getPayments,
  upsertPayment,
  getSettings: getCoachSettingsRow,
  updateSettings: updateCoachSettings,
};
